import React, { useRef, useEffect, useMemo } from 'react';
import * as echarts from 'echarts';

const DEFAULT_CHART_HEIGHT = 300;
const GRID_VERTICAL_PADDING = 40; // grid top(20) + bottom(20)
const HEAT_COLORS = ['#dacd57', '#e0b251', '#e7974b', '#eb8547', '#f45c3d'];

const heightOfJson = (json) => {
  const recur = (item, level = 0) => {
    if ((item.children || []).length === 0) {
      return level;
    }
    let maxLevel = level;
    for (const child of item.children) {
      const tempLevel = recur(child, level + 1);
      maxLevel = Math.max(maxLevel, tempLevel);
    }
    return maxLevel;
  };
  return recur(json);
};

/**
 * 卡顿火焰图。
 *
 * 默认表现（DataHead / 对比弹窗）：固定 300px 高，叶子在上、根在下。
 * 上报详情采样火焰图通过下列可选 prop 切换为「每层最小高度 + 容器滚动 + 根在上」：
 * - minBlockHeight: 每层最小高度(px)。传入后画布按树深度拉高，避免层数多时被压扁
 * - maxHeight: 可视区域上限，超出后出现纵向滚动条
 * - rootOnTop: true 时根在上、叶子在下（与滚动条默认停在顶部配合）
 */
const JankFlameGraph = ({ data, minBlockHeight, maxHeight, rootOnTop = false, highlightKeyword = '' }) => {
  const chartRef = useRef(null);

  const chartHeight = useMemo(() => {
    if (!data || !minBlockHeight) {
      return DEFAULT_CHART_HEIGHT;
    }
    const levels = heightOfJson(data);
    return Math.max(DEFAULT_CHART_HEIGHT, (levels + 1) * minBlockHeight + GRID_VERTICAL_PADDING);
  }, [data, minBlockHeight]);

  useEffect(() => {
    if (!data?.value || !chartRef.current) {
      return undefined;
    }

    // 必须放在 effect 局部，同一页面可能同时有 DataHead 和上报详情两份实例
    const originalMaxLevel = heightOfJson(data);

    const filterJson = (json, pathList = []) => {
      if (pathList.length <= 1) {
        return json;
      }
      // 先处理原始数据，添加pathList
      const pathString = pathList.join();
      const pathDataTree = structuredClone(json);
      const handleRecur = (item, parent = null) => {
        item.pathList = parent ? parent?.pathList?.concat(item.name) :  [item.name]; // 当前路径-用于火焰图点击区分相同name
        for (const child of item.children || []) {
          handleRecur(child,  item);
        }
      };
      handleRecur(pathDataTree);

      // 针对pathList找到对应的位置
      const recur = (item, pathString) => {
        if (item.pathList.join() === pathString) {
          return item;
        }
        for (const child of item.children || []) {
          const temp = recur(child, pathString);
          if (temp) {
            item.children = [temp];
            item.value = temp.value; // change the parents' values
            return item;
          }
        }
      };
      return recur(pathDataTree, pathString);
    };

    const keyword = (highlightKeyword || '').trim().toLowerCase();
    const recursionJson = (jsonObj, pathList) => {
      const flameData = [];
      const filteredJson = filterJson(structuredClone(jsonObj), pathList);
      const rootVal = jsonObj.value;
      const recur = (item, start = 0, level = 0, parent) => {
        const { value, name, avg, cnt, avgPF } = item;
        const colorIndex = Math.floor((value / rootVal) / 0.2);
        const color = HEAT_COLORS[colorIndex] || '#dacd57';
        const highlighted = keyword && String(name || '').toLowerCase().includes(keyword) ? 1 : 0;
        const temp = {
          name: name,
          // [level, start_val, end_val, name, percentage, avg, cnt, avgPF, highlighted, reportHits]
          value: [
            level,
            start,
            start + value,
            item.name,
            (value / rootVal) * 100,
            avg,
            cnt,
            avgPF,
            highlighted,
            item.reportHits || 0,
          ],
          pathList: level === 0 ?  [name] : parent?.pathList?.concat(item.name), // 当前路径-用于火焰图点击区分相同name
          itemStyle: {
            color: level === 0 ? '#e3a34e' : color,
          },
        };
        flameData.push(temp);
        let prevStart = start;
        for (const child of item.children || []) {
          recur(child, prevStart, level + 1, temp);
          prevStart = prevStart + child.value;
        }
      };
      recur(filteredJson);
      return flameData;
    };

    const renderItem = (params, api) => {
      const level = api.value(0);
      const start = api.coord([api.value(1), level]);
      const end = api.coord([api.value(2), level]);
      const height = ((api.size && api.size([0, 1])) || [0, 20])[1];
      const width = end[0] - start[0];

      return {
        type: 'rect',
        transition: ['shape'],
        shape: {
          x: start[0],
          y: start[1] - height / 2,
          width,
          height: height - 2 /* itemGap */,
          r: 2,
        },
        style: {
          fill: api.visual('color'),
          stroke: api.value(8) ? '#000' : 'transparent',
          lineWidth: api.value(8) ? 1.5 : 0,
        },
        emphasis: {
          style: {
            stroke: '#000',
          },
        },
        textConfig: {
          position: 'insideLeft',
        },
        textContent: {
          style: {
            text: api.value(3),
            fontFamily: 'Verdana',
            fill: '#000',
            width: width - 4,
            overflow: 'truncate',
            ellipsis: '..',
            truncateMinChar: 1,
          },
          emphasis: {
            style: {
              stroke: '#000',
              lineWidth: 0.5,
            },
          },
        },
      };
    };

    const option = {
      tooltip: {
        formatter: (params) => {
          const name = `${params.marker} ${params.value[3]}`;
          const avgPF = params.value[7];
          const reportHits = params.value[9];
          let text = name;
          if (avgPF) {
            text += `: (avgPF=${Number(avgPF).toFixed(2)}ms)`;
          }
          if (reportHits) {
            text += `<br/>覆盖 ${reportHits} 条上报`;
          }
          return text;
        },
      },
      grid: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      },
      xAxis: {
        show: false,
        max: data.value
      },
      yAxis: {
        show: false,
        max: originalMaxLevel,
        min: 0,
        inverse: rootOnTop, // true：根在上、叶子在下；false：叶子在上、根在下
      },
      series: [
        {
          type: 'custom',
          renderItem,
          encode: {
            x: [1, 2],
            y: 0,
          },
          data: recursionJson(data),
          animation: false, // 禁用动画
          animationDuration: 0, // 设置动画持续时间为0
        },
      ],
    };

    const myChart = echarts.init(chartRef.current);
    myChart.setOption(option);
    const onClick = (params) => {
      const rootData = recursionJson(data, params.data.pathList);
      if (!rootData.length) {
        return;
      }
      const rootValue = rootData[0].value[2];
      // 固定高度模式：缩小 y 轴让放大后的子树铺满 300px（原 DataHead 行为）
      // 每层最小高度模式：保持原始层数，避免子树被拉成超高的块
      const zoomYMax = minBlockHeight
        ? originalMaxLevel
        : rootData.reduce((max, item) => Math.max(max, item.value[0]), 0);
      myChart.setOption({
        ...option,
        xAxis: { max: rootValue, show: false, },
        yAxis: {
          show: false,
          max: zoomYMax,
          min: 0,
          inverse: rootOnTop,
        },
        series: {
          type: 'custom',
          renderItem,
          encode: {
            x: [1, 2],
            y: 0,
          },
          data: rootData
        },
      });
    };
    myChart.on('click', onClick);

    return () => {
      myChart.off('click', onClick);
      myChart.dispose();
    };
  }, [data, chartHeight, rootOnTop, minBlockHeight, highlightKeyword]);

  const chartEl = (
    <div
      ref={chartRef}
      style={minBlockHeight
        ? { width: '100%', height: chartHeight }
        : { width: '100%', height: `${DEFAULT_CHART_HEIGHT}px`, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}
    />
  );

  if (maxHeight == null) {
    return chartEl;
  }

  return (
    <div style={{ width: '100%', maxHeight, overflowY: 'auto', overflowX: 'hidden' }}>
      {chartEl}
    </div>
  );
};

export default JankFlameGraph;
